import { Trans, useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import ExportCareerDiagnosePage from "./export/ExportCareerDiagnosePage";
import ExportChapterCover from "./export/ExportChapterCover";
import ExportCoverPage from "./export/ExportCoverPage";
import { useEffect, useState } from "react";
import { StageProps } from "./StageProps";
import { getDiagnosticById } from "shared/service/StrmService";
import CareerProfile from "./CareerProfile";
import riscoFormula from "shared/assets/images/career-diagnose/risco-formula.png";
import channelAnalysisImg from "shared/assets/images/career-diagnose/channel-analysis.png";
import channelFocus from "shared/assets/images/career-diagnose/channel-focus.svg";
import { Col, Dropdown, Modal, Progress, Row, Spin, Table } from "antd";
import { Charts } from "shared/components";
import { ReactComponent as PotencialFormula } from "shared/assets/images/career-diagnose/potencial-formula.svg";
import { capitalize } from "lodash";
import CareerCatalogAnalysis, { releaseColumns } from "./CareerCatalogAnalysis";
import { StarOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  ComparisonBox,
  ComparisonValue,
  ComparisonBarChart,
  ComparisonExplain,
  ChannelCircle,
} from "./components";
import ComparisonLegend from "./components/ComparisonLegend";
import MouseDisclaimer from "./components/MouseDisclaimer";
import { normalDistribuition } from "shared/constants/normalDistribuition";
import CareerDiagnoseMobile from "./mobile/CareerDiagnoseMobile";
import { isMobile } from "react-device-detect";
import CriticalStrongPointsEmpty from "./components/CriticalStrongPointsEmpty";
import { splitArrayByChunk } from "shared/helpers/array-functions";
import moment from "moment";
import {
  channelAnalysisValues,
  comparisonColors,
  gaugeValues,
  getrevenueColorsByindex,
  revenueColorsValue,
  similarityColors,
} from "./shared-conts";
import html2canvas from "html2canvas";
import { useSelector } from "react-redux";
import { UserTypeEnum } from "shared/enums/UserTypeEnum";
import "./CareerDiagnose.scss";

const antIcon = (
  <LoadingOutlined
    style={{ fontSize: 48, color: "#ececec" }}
    spin
    className="spin-ref"
  />
);

const ids: any = [
  "capa",
  "introducao-2",
  "introducao-3",
  "jornada-construcao-carreira-1",
  "jornada-construcao-carreira-2",
  "jornada-construcao-carreira-3",
  "jornada-construcao-carreira-4",
  "jornada-construcao-carreira-5",

  "dados-publicos-1",
  "dados-publicos-2",
  "dados-publicos-3",
  "dados-publicos-4",
  "dados-publicos-5",

  "dados-privados-1",
  "dados-privados-2",
  "dados-privados-3",
  "dados-privados-4",
  "dados-privados-5",
  "dados-privados-6",
  "dados-privados-7",
  "dados-privados-8",
  "dados-privados-9",
  "dados-privados-10",
  "dados-privados-11",
  "dados-privados-12",
  "dados-privados-13",
  "dados-privados-14",
  "dados-privados-15",
  "dados-privados-16",
  "dados-privados-17",
  "dados-privados-18",

  "conclusao-1",
  "conclusao-2",
  "conclusao-3",
  "conclusao-4",
];

const ErrorMessage: React.FC<{ id: string }> = ({ id }) => {
  return (
    <Trans
      i18nKey="pages.CareerDiagnose.errorText"
      ns="radar"
      values={{ id }}
    />
  );
};

const { error } = Modal;
const CareerDiagnose: React.FC<RouteComponentProps> = (props) => {
  const { match } = props;
  const { t, i18n } = useTranslation("radar");
  const { id }: any = match.params;
  const loggedUser = useSelector((state: any) => state.users.loggedUser);
  const admin = loggedUser?.typeAccess === UserTypeEnum.AdminStrm;
  const [stageProps, setStageProps] = useState<StageProps>();
  const [generalImages, setGeneralImages] = useState<any>();
  const [diagError, setDiagError] = useState<boolean>(false);
  const [stage, setStage] = useState<number>();
  const [diagnostic, setDiagnostic] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [releaseChunks, setReleaseChunks] = useState<any[]>([]);
  const [audioFeatures, setAudioFeatures] = useState<any>({
    categories: [],
    series: [],
  });
  const artistName =
    diagnostic?.publicData?.artistRankDetailInfo?.artistMetadata?.artistName;
  const { lastUpdate } = diagnostic?.privateData || {};

  const { performanceDetail, careerStageChart, riskIndex } =
    diagnostic?.privateData || {};

  const {
    revenueProfile,
    momentumIndex,
    criticalPoints,
    strengths,
    channelAnalysis,
  } = diagnostic?.conclusions || {};

  const { artistRankDetailInfo } = diagnostic?.publicData || {};
  const spotify =
    (artistRankDetailInfo?.artistMetadata?.artistIdSpotify && true) || false;
  const youtube =
    (artistRankDetailInfo?.artistMetadata?.channelIdYoutube && true) || false;
  const instagram =
    (artistRankDetailInfo?.artistMetadata?.usernameInstagram && true) || false;

  const profileRevenueInformed =
    (revenueProfile?.composer?.ammount || 0) +
      (revenueProfile?.shows?.ammount || 0) +
      (revenueProfile?.publicity?.ammount || 0) +
      (revenueProfile?.streaming?.ammount || 0) >
    0;

  const getKeyByGender = (
    key: "high" | "medium" | "low",
    gender: "male" | "female" = "male"
  ) => t(`pages.CareerDiagnose.${key}`, { context: gender });

  const getTipsByItem = (item: string) => {
    const { failSensation, successSensation, whatToDo } = t(
      `pages.CareerDiagnose.privateData${item}`,
      {
        returnObjects: true,
      }
    ) as any;
    return { failSensation, successSensation, whatToDo };
  };

  const getAvailableMedia = () => {
    const media: string[] = [
      spotify ? "Spotify" : "",
      youtube ? "Youtube" : "",
      instagram ? "Instagram" : "",
    ].filter((media) => media);

    return media.length > 1
      ? `${media.slice(0, -1).join(", ")} ${t("pages.CareerDiagnose.e")} ${
          media[media.length - 1]
        }`
      : media.length === 1
      ? media[0]
      : "";
  };

  const getRevenueInfo: any = () => {
    if (!revenueProfile) return { series: [] };
    const sum =
      (revenueProfile?.composer.ammount || 0) +
      (revenueProfile?.shows.ammount || 0) +
      (revenueProfile?.publicity.ammount || 0) +
      (revenueProfile?.streaming.ammount || 0);

    const composer = [sum - revenueProfile?.composer.ammount || 0, sum];
    const shows = [
      composer[0] - revenueProfile?.shows.ammount || 0,
      composer[0],
    ];
    const publicity = [
      shows[0] - revenueProfile?.publicity.ammount || 0,
      shows[0],
    ];
    const streaming = [
      publicity[0] - revenueProfile?.streaming.ammount || 0,
      publicity[0],
    ];
    return {
      series: [
        {
          data: [
            { x: t("pages.CareerDiagnose.total"), y: [0, sum] },
            {
              x: t("pages.CareerDiagnose.composer"),
              y: composer,
            },
            {
              x: t("pages.CareerDiagnose.shows"),
              y: shows,
            },
            {
              x: t("pages.CareerDiagnose.publicity"),
              y: publicity,
            },
            {
              x: t("pages.CareerDiagnose.streaming"),
              y: streaming,
            },
          ],
        },
      ],
    };
  };

  const handleDownloadAll = () => {
    ids.forEach(async (id: any) => {
      const element: any = document.getElementById(id);
      const canvas = await html2canvas(element);
      const link = document.createElement("a");
      link.download = `${id}.jpg`;
      link.href = canvas.toDataURL("image/jpeg");
      link.click();
    });
  };

  const handleDownload = async (id: string) => {
    setLoading(true);
    const element: any = document.getElementById(id);
    const canvas = await html2canvas(element);
    const link = document.createElement("a");
    link.download = `${id}.jpg`;
    link.href = canvas.toDataURL("image/jpeg");
    link.click();
    setLoading(false);
  };

  const getRevenueProfile = () =>
    revenueProfile?.artistProfile === "Performer/Influencer/Digital/Composer"
      ? t("pages.CareerDiagnose.noClassification")
      : revenueProfile?.artistProfile;

  useEffect(() => {
    const getStageProps = async () => {
      const image1Response = await import(
        `shared/assets/images/career-diagnose/stage/${i18n.language}/${stage}/image1.png`
      );
      const image2Response = await import(
        `shared/assets/images/career-diagnose/stage/${i18n.language}/${stage}/image2.png`
      );
      const image3Response = await import(
        `shared/assets/images/career-diagnose/stage/${i18n.language}/${stage}/image3.png`
      );
      const image4Response = await import(
        `shared/assets/images/career-diagnose/stage/${i18n.language}/${stage}/image4.png`
      );
      const image5Response = await import(
        `shared/assets/images/career-diagnose/stage/${i18n.language}/${stage}/image5.png`
      );

      const stageI18n: any = t(`pages.CareerDiagnose.stage.${stage}`, {
        returnObjects: true,
      });
      setStageProps({
        image1: image1Response.default,
        image2: image2Response.default,
        image3: image3Response.default,
        image4: image4Response.default,
        image5: image5Response.default,
        ...stageI18n,
      });
    };

    if (stage && stage > 0) {
      getStageProps();
    }
  }, [stage, i18n.language, t]);

  useEffect(() => {
    const getGeneralImages = async () => {
      const algorithmStrm = await import(
        `shared/assets/images/career-diagnose/general/${i18n.language}/algorithm-strm.png`
      );
      setGeneralImages({
        algorithmStrm: algorithmStrm.default,
      });
    };

    getGeneralImages();
  }, [i18n.language]);

  useEffect(() => {
    if (diagnostic) {
      if (diagnostic?.publicData?.artistRankDetailInfo?.audioFeatures) {
        const { categories, series, bpm } =
          diagnostic?.publicData?.artistRankDetailInfo?.audioFeatures;
        setAudioFeatures({ categories, series: series.slice(0, 4), bpm });
      }
      if (diagnostic?.publicData?.lastReleases?.artistReleases) {
        diagnostic?.publicData?.lastReleases?.artistReleases?.sort(
          (a: any, b: any) => b.popularity - a.popularity
        );
        const releasesWithTime =
          diagnostic?.publicData?.lastReleases?.artistReleases?.map(
            (release: any) => ({
              ...release,
              releaseDateUnix: moment(release.releaseDate.dateTruncated).unix(),
            })
          );
        setReleaseChunks(
          splitArrayByChunk(
            releasesWithTime?.sort(
              (a: any, b: any) => b.releaseDateUnix - a.releaseDateUnix
            ),
            40
          )
        );
      }
    }
  }, [diagnostic]);

  useEffect(() => {
    const showErrorDiagnose = () => {
      setLoading(false);
      setDiagError(true);
      error({
        width: 800,
        closable: false,
        title: t("pages.CareerDiagnose.errorTitle"),
        content: <ErrorMessage id={id} />,
        okButtonProps: {
          style: { display: "none" },
        },
      });
    };

    const getDiagnostic = async () => {
      try {
        setLoading(true);
        const response: any = await getDiagnosticById(id);
        const careerStage = response?.publicData?.stageCareer;
        if (careerStage) {
          setStage(careerStage);
          setDiagnostic(response);
        } else showErrorDiagnose();
        setLoading(false);
      } catch {
        showErrorDiagnose();
      }
    };
    getDiagnostic();
  }, [id, t]);

  if (diagError) return <div className="diagnostic-error-page" />;

  if (isMobile)
    return (
      <CareerDiagnoseMobile
        loading={loading}
        artistName={artistName}
        lastUpdate={lastUpdate}
        diagnostic={diagnostic}
        stage={stage}
        audioFeatures={audioFeatures}
        privateData={diagnostic?.privateData}
        releaseChunks={releaseChunks}
        generalImages={generalImages}
      />
    );
  return (
    <Spin
      spinning={loading}
      tip={t("pages.CareerDiagnose.loadingMessage")}
      indicator={antIcon}
    >
      <Dropdown
        menu={{
          items: ids.map((item: any) => ({
            label: item,
            key: item,
          })),
          onClick: (info: any) => handleDownload(info.key),
        }}
        trigger={["contextMenu"]}
        disabled={!admin}
        dropdownRender={(menu) => <div style={{ height: 500 }}>{menu}</div>}
        overlayStyle={{ maxHeight: 500, overflow: "auto" }}
      >
        <div className="career-diagnose">
          <button style={{ display: "none" }} onClick={handleDownloadAll}>
            Download All Images
          </button>
          <ExportCoverPage
            id="capa"
            artistName={artistName}
            lastUpdate={lastUpdate}
          />
          <ExportCareerDiagnosePage
            className="summary"
            id="introducao-2"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="summary-page">
              <h1>{t("pages.CareerDiagnose.summaryTitle")}</h1>
              <ol>
                <li>
                  <a href="#introducao-3">
                    {t("pages.CareerDiagnose.summaryIntro")}
                  </a>
                </li>
                <li>
                  <a href="#jornada-construcao-carreira-2">
                    {t("pages.CareerDiagnose.summaryJourney")}
                  </a>
                </li>
                <li>
                  <a href="#dados-publicos-2">
                    {t("pages.CareerDiagnose.summaryPublicData")}
                  </a>
                </li>
                <li>
                  <a href="#dados-privados-2">
                    {t("pages.CareerDiagnose.summaryPrivateData")}
                  </a>
                </li>
                <li>
                  <a href="#conclusao-2">
                    {t("pages.CareerDiagnose.summaryConclusion")}
                  </a>
                </li>
              </ol>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="intro"
            id="introducao-3"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="intro-page">
              <h1>{t("pages.CareerDiagnose.introTitle")}</h1>
              <Trans
                i18nKey="pages.CareerDiagnose.introText"
                ns="radar"
                values={{
                  artistName,
                  media: getAvailableMedia().toUpperCase(),
                }}
                components={{ bold: <strong />, list: <ul />, item: <li /> }}
              />
            </div>
          </ExportCareerDiagnosePage>
          <ExportChapterCover
            id="jornada-construcao-carreira-1"
            title={t("pages.CareerDiagnose.careerJourneyTitle")}
          />
          <ExportCareerDiagnosePage
            className="journey-1"
            id="jornada-construcao-carreira-2"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="career-stage">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.careerStageTitle").toUpperCase()}
              </h1>
              <img src={stageProps?.image1} alt="Gráfico Estágio" />
              <h1 style={{ marginTop: 32 }}>
                {stageProps?.journeyTitle} {` - `}
                {stageProps?.journeySubtitle}
              </h1>
              <p style={{ columnCount: 2, textAlign: "justify", fontSize: 18 }}>
                <Trans
                  i18nKey={`pages.CareerDiagnose.stage.${stage}.journeyContent`}
                  ns="radar"
                />
              </p>
              <div style={{ display: "flex", gap: 32, margin: "32px 0" }}>
                <div style={{ width: "35%" }}>
                  <img
                    style={{
                      width: "100%",
                      maxHeight: 250,
                      objectFit: "contain",
                    }}
                    src={stageProps?.image2}
                    alt="Zoom no estágio específico"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <h1
                    className="texto-colorido"
                    style={{
                      textAlign: "end",
                      fontSize: 24,
                      textTransform: "uppercase",
                    }}
                  >
                    {t("pages.CareerDiagnose.bigChallenge")}
                    {` = ${stageProps?.bigChallenge}`}
                  </h1>
                  <p style={{ textAlign: "justify", fontSize: 18 }}>
                    <Trans
                      i18nKey={`pages.CareerDiagnose.stage.${stage}.bigChallengeText1`}
                      ns="radar"
                    />
                  </p>
                </div>
              </div>
              <p style={{ columnCount: 2, textAlign: "justify", fontSize: 18 }}>
                <Trans
                  i18nKey={`pages.CareerDiagnose.stage.${stage}.bigChallengeText2`}
                  ns="radar"
                />
              </p>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="journey-1"
            id="jornada-construcao-carreira-3"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="operation-model-mindset">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.operationModelMindsetTitle")}
              </h1>
              <img src={stageProps?.image3} alt="Modelo de Operação" />
              <h1 style={{ marginTop: 32 }}>{stageProps?.operationTitle}</h1>
              <p style={{ textAlign: "justify", fontSize: 18 }}>
                <Trans
                  i18nKey={`pages.CareerDiagnose.stage.${stage}.operationText1`}
                  ns="radar"
                />
              </p>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="journey-1"
            id="jornada-construcao-carreira-4"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="investment-cycles">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.investmenCycles")}
              </h1>
              <img
                src={stageProps?.image4}
                alt="Gráfico de ciclos de investimento"
              />
              <h1 style={{ marginTop: 32 }}>
                {stageProps?.investmentCycleTitle}
              </h1>
              <p style={{ textAlign: "justify", fontSize: 18 }}>
                <Trans
                  i18nKey={`pages.CareerDiagnose.stage.${stage}.investmentCycleText`}
                  ns="radar"
                />
              </p>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="journey-1"
            id="jornada-construcao-carreira-5"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="goals-objectives">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.goalsObjectives")}
              </h1>
              <img
                src={stageProps?.image5}
                alt="Gráfico de objetivos e metas"
              />
              <p style={{ textAlign: "justify", fontSize: 18, marginTop: 72 }}>
                <Trans
                  i18nKey={`pages.CareerDiagnose.stage.${stage}.objectiveGoalsText1`}
                  ns="radar"
                />
              </p>
              <h1>{t("pages.CareerDiagnose.ways")}</h1>
              <p style={{ textAlign: "justify", fontSize: 18 }}>
                <Trans
                  i18nKey={`pages.CareerDiagnose.stage.${stage}.objectiveGoalsText2`}
                  ns="radar"
                />
              </p>
            </div>
          </ExportCareerDiagnosePage>
          <ExportChapterCover
            id="dados-publicos-1"
            title={t("pages.CareerDiagnose.publicDataTitle")}
          />
          <ExportCareerDiagnosePage
            className="public-data-1"
            id="dados-publicos-2"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <CareerProfile publicData={diagnostic?.publicData} />
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="public-data-1"
            id="dados-publicos-3"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <CareerCatalogAnalysis
              {...diagnostic?.publicData}
              spotify={spotify}
            />
          </ExportCareerDiagnosePage>
          {releaseChunks.map((chunk, index) => (
            <ExportCareerDiagnosePage
              className="public-data-1"
              id={
                index === 0 ? "dados-publicos-4" : `dados-pulbucos-${5 + index}`
              }
              shade="black"
              artistName={artistName}
              lastUpdate={lastUpdate}
              key={`releases-page-${index}`}
            >
              <div>
                <h1 className="title">
                  {t("pages.CareerDiagnose.tracksList")}
                </h1>
                <Table
                  columns={releaseColumns(t)}
                  pagination={false}
                  dataSource={chunk}
                />
              </div>
            </ExportCareerDiagnosePage>
          ))}

          <ExportCareerDiagnosePage
            className="public-data-1"
            id="dados-publicos-5"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="analysis-of-audio">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.analysisAudioTitle")}
              </h1>
              <h1 style={{ marginTop: 32 }}>
                {t("pages.CareerDiagnose.analysisAudioSubTitle")}
              </h1>
              <p style={{ textAlign: "justify", fontSize: 18 }}>
                <Trans
                  i18nKey="pages.CareerDiagnose.analysisAudioText1"
                  ns="radar"
                />
              </p>
              {spotify ? (
                <span
                  style={{
                    marginBottom: 32,
                    textAlign: "end",
                    textTransform: "uppercase",
                  }}
                >
                  {t("pages.CareerDiagnose.genreClickDisclaimer")}
                </span>
              ) : (
                <div className="disclaimer-audio-features-no-spotify">
                  <span>{t("pages.CareerDiagnose.audioFeatureNoSpotify")}</span>
                </div>
              )}
              <Charts.RadarChart
                cor={similarityColors}
                width={200}
                height={550}
                multiplyBy100
                textColor="#000"
                legendStyle={{
                  fontSize: "20px",
                  position: "top",
                  markers: {
                    radius: 6,
                    height: 18,
                    width: 18,
                  },
                  itemMargin: {
                    horizontal: 48,
                  },
                }}
                tooltip={
                  audioFeatures?.categories.map((serie: any) => ({
                    text: t(`pages.ArtistProfile.${serie}`),
                    name: serie,
                    bpm: audioFeatures?.bpm,
                  })) || []
                }
                {...audioFeatures}
              />
              <MouseDisclaimer style={{ marginBottom: 32, textAlign: "end" }} />
              <p style={{ textAlign: "justify", fontSize: 18 }}>
                <Trans
                  i18nKey="pages.CareerDiagnose.analysisAudioText2"
                  ns="radar"
                />
              </p>
            </div>
          </ExportCareerDiagnosePage>
          <ExportChapterCover
            id="dados-privados-1"
            title={t("pages.CareerDiagnose.privateDataTitle")}
          />
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-2"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="algorithm-strm">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.algorithmStrmTitle")}
              </h1>
              <p
                style={{
                  columnCount: 2,
                  textAlign: "justify",
                  fontSize: 22,
                  marginTop: 72,
                }}
              >
                <Trans
                  i18nKey={`pages.CareerDiagnose.algorithmStrmText`}
                  ns="radar"
                />
              </p>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 64,
                }}
              >
                <img
                  src={generalImages?.algorithmStrm}
                  alt="Algorítmo Strm"
                  style={{ width: "70%" }}
                />
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-3"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="potencial-analysis">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.potencialAnalysisTitle")}
              </h1>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <PotencialFormula style={{ width: 400, height: 100 }} />
              </div>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                  marginTop: 32,
                }}
              >
                <Trans
                  i18nKey="pages.CareerDiagnose.potencialAnalysisText1"
                  ns="radar"
                />
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div className="radar-chart-card">
                  <Charts.RadarChart
                    cor={comparisonColors}
                    width={70}
                    textColor="#000"
                    legend={false}
                    labelFontSize="12px"
                    {...(performanceDetail?.music?.metricChart || {
                      categories: [],
                      series: [],
                    })}
                    names={[
                      artistName,
                      t("pages.CareerDiagnose.minimumRequired"),
                    ]}
                    markerSize={2}
                    strokeWidth={1}
                    strokeColor="#fff"
                    max={5}
                    tickAmount={5}
                  />
                  {t("pages.CareerDiagnose.music")}
                  <ComparisonBox {...performanceDetail?.music} />
                </div>
                <div className="radar-chart-card">
                  <Charts.RadarChart
                    cor={comparisonColors}
                    width={70}
                    textColor="#000"
                    legend={false}
                    labelFontSize="12px"
                    {...(performanceDetail?.storytelling?.metricChart || {
                      categories: [],
                      series: [],
                    })}
                    names={[
                      artistName,
                      t("pages.CareerDiagnose.minimumRequired"),
                    ]}
                    markerSize={2}
                    strokeWidth={1}
                    strokeColor="#fff"
                    max={5}
                    tickAmount={5}
                  />
                  {t("pages.CareerDiagnose.stories")}
                  <ComparisonBox {...performanceDetail?.storytelling} />
                </div>
                <div className="radar-chart-card">
                  <Charts.RadarChart
                    cor={comparisonColors}
                    width={70}
                    textColor="#000"
                    legend={false}
                    labelFontSize="12px"
                    {...(performanceDetail?.audience?.metricChart || {
                      categories: [],
                      series: [],
                    })}
                    names={[
                      artistName,
                      t("pages.CareerDiagnose.minimumRequired"),
                    ]}
                    markerSize={2}
                    strokeWidth={1}
                    strokeColor="#fff"
                    max={5}
                    tickAmount={5}
                  />
                  {t("pages.CareerDiagnose.audience")}
                  <ComparisonBox {...performanceDetail?.audience} />
                </div>
              </div>
              <MouseDisclaimer
                style={{ marginTop: 32, marginBottom: 0, textAlign: "end" }}
              />
              <h1 className="texto-colorido title" style={{ marginTop: 72 }}>
                {t("pages.CareerDiagnose.performanceAnalysisTitle")}
              </h1>{" "}
              <h1 style={{ textAlign: "center", marginTop: 0 }}>
                {t("pages.CareerDiagnose.careerStage", { stage: stage })}
              </h1>
              <div className="progress-stage">
                <div>
                  <span className="stage-circle">
                    {((careerStageChart?.init || 0) * 100).toFixed(0)}
                  </span>
                </div>
                <Progress
                  percent={Math.ceil(
                    artistRankDetailInfo?.strmIndexes?.stageProgress * 100 || 0
                  )}
                  strokeColor="#75C594"
                  trailColor="#538B66"
                  strokeWidth={30}
                  style={{ marginTop: 12 }}
                  showInfo={false}
                />
                <div>
                  <span className="stage-circle">
                    {((careerStageChart?.end || 0) * 100).toFixed(0)}
                  </span>
                </div>
              </div>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                  marginTop: 32,
                }}
              >
                <Trans
                  i18nKey="pages.CareerDiagnose.performanceAnalysisText"
                  ns="radar"
                />
              </p>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                }}
              >
                <Trans
                  i18nKey={`pages.CareerDiagnose.stage.${stage}.performanceAnalysis`}
                  values={{
                    stage,
                    progress: (
                      (careerStageChart?.currentProgress || 0) * 100
                    ).toFixed(),
                  }}
                  ns="radar"
                />
              </p>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-4"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="music-related-info">
              <h1 className="white-text title">
                {t("pages.CareerDiagnose.musicRelatedInfoTitle")}
              </h1>
              <p style={{ marginTop: 32, fontSize: 18 }}>
                <Trans
                  i18nKey="pages.CareerDiagnose.musicRelatedInfoText"
                  ns="radar"
                />
              </p>
              <p style={{ fontSize: 18 }}>
                {performanceDetail?.music?.evaluationOfAllPoints}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <Charts.RadarChart
                cor={comparisonColors}
                width={250}
                textColor="#fff"
                min={0}
                max={5}
                tickAmount={5}
                legend={false}
                {...(performanceDetail?.music?.metricChart || {
                  categories: [],
                  series: [],
                })}
                names={[artistName, t("pages.CareerDiagnose.minimumRequired")]}
              />
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonBox big {...performanceDetail?.music} />
              </div>
              <MouseDisclaimer style={{ marginTop: 72, textAlign: "end" }} />
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-5"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.music?.subMetric?.concept || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.music?.subMetric?.concept || {})}
                  text={t("pages.CareerDiagnose.concept")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Concept")}
                  {...(performanceDetail?.music?.subMetric?.concept || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.music?.subMetric?.repertoire || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.music?.subMetric?.repertoire || {})}
                  text={t("pages.CareerDiagnose.repertoire")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={t("pages.CareerDiagnose.fast")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Repertoire")}
                  {...(performanceDetail?.music?.subMetric?.repertoire || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.music?.subMetric?.production || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.music?.subMetric?.production || {})}
                  text={t("pages.CareerDiagnose.production")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={t("pages.CareerDiagnose.fast")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Production")}
                  {...(performanceDetail?.music?.subMetric?.production || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.music?.subMetric?.show || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.music?.subMetric?.show || {})}
                  text={t("pages.CareerDiagnose.show")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("medium", "female")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Show")}
                  {...(performanceDetail?.music?.subMetric?.show || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.music?.subMetric?.schedule || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.music?.subMetric?.schedule || {})}
                  text={t("pages.CareerDiagnose.agenda")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("low", "female")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("high")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Agenda")}
                  {...(performanceDetail?.music?.subMetric?.schedule || {})}
                />
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-6"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="music-related-info">
              <h1 className="white-text title">
                {t("pages.CareerDiagnose.howYouAreRememberedTitle")}
              </h1>
              <p style={{ marginTop: 32, fontSize: 18 }}>
                <Trans
                  i18nKey="pages.CareerDiagnose.howYouAreRememberedText"
                  ns="radar"
                />
              </p>
              <p style={{ fontSize: 18 }}>
                {performanceDetail?.storytelling?.evaluationOfAllPoints}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <Charts.RadarChart
                cor={comparisonColors}
                width={250}
                textColor="#fff"
                legend={false}
                min={0}
                max={5}
                tickAmount={5}
                {...(performanceDetail?.storytelling?.metricChart || {
                  categories: [],
                  series: [],
                })}
                names={[artistName, t("pages.CareerDiagnose.minimumRequired")]}
              />
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonBox big {...performanceDetail?.storytelling} />
              </div>
              <MouseDisclaimer style={{ marginTop: 172, textAlign: "end" }} />
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-7"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.storytelling?.subMetric?.brand ||
                      {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.storytelling?.subMetric?.brand || {})}
                  text={t("pages.CareerDiagnose.brand")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Brand")}
                  {...(performanceDetail?.storytelling?.subMetric?.brand || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.storytelling?.subMetric?.identity ||
                      {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.storytelling?.subMetric?.identity ||
                    {})}
                  text={t("pages.CareerDiagnose.identity")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={t("pages.CareerDiagnose.fast")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Identity")}
                  {...(performanceDetail?.storytelling?.subMetric?.identity ||
                    {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.storytelling?.subMetric
                      ?.narrative || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.storytelling?.subMetric?.narrative ||
                    {})}
                  text={t("pages.CareerDiagnose.narrative")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Narrative")}
                  {...(performanceDetail?.storytelling?.subMetric?.narrative ||
                    {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.storytelling?.subMetric?.content ||
                      {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.storytelling?.subMetric?.content ||
                    {})}
                  text={t("pages.CareerDiagnose.content")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Content")}
                  {...(performanceDetail?.storytelling?.subMetric?.content ||
                    {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.storytelling?.subMetric
                      ?.adherence || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.storytelling?.subMetric?.adherence ||
                    {})}
                  text={t("pages.CareerDiagnose.adhesion")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Adhesion")}
                  {...(performanceDetail?.storytelling?.subMetric?.adherence ||
                    {})}
                />
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-8"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="music-related-info">
              <h1 className="white-text title">
                {t("pages.CareerDiagnose.noAudienceEcoTitle")}
              </h1>
              <p style={{ marginTop: 32, fontSize: 18 }}>
                <Trans
                  i18nKey="pages.CareerDiagnose.noAudienceEcoText"
                  ns="radar"
                />
              </p>
              <p style={{ fontSize: 18 }}>
                {performanceDetail?.audience?.evaluationOfAllPoints}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <Charts.RadarChart
                cor={comparisonColors}
                width={250}
                textColor="#fff"
                legend={false}
                max={5}
                tickAmount={5}
                {...(performanceDetail?.audience?.metricChart || {
                  categories: [],
                  series: [],
                })}
                names={[artistName, t("pages.CareerDiagnose.minimumRequired")]}
              />
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonBox big {...performanceDetail?.audience} />
              </div>
              <MouseDisclaimer style={{ marginTop: 172, textAlign: "end" }} />
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-9"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.audience?.subMetric?.purchased ||
                      {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.audience?.subMetric?.purchased || {})}
                  text={t("pages.CareerDiagnose.buyed")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={t("pages.CareerDiagnose.fast")}
                  evolutionCost={getKeyByGender("high")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Buyed")}
                  {...(performanceDetail?.audience?.subMetric?.purchased || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.audience?.subMetric?.social || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.audience?.subMetric?.social || {})}
                  text={t("pages.CareerDiagnose.social")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Social")}
                  {...(performanceDetail?.audience?.subMetric?.social || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.audience?.subMetric?.viral || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.audience?.subMetric?.viral || {})}
                  text={t("pages.CareerDiagnose.viral")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("medium")}
                  {...getTipsByItem("Viral")}
                  {...(performanceDetail?.audience?.subMetric?.viral || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.audience?.subMetric?.won || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.audience?.subMetric?.won || {})}
                  text={t("pages.CareerDiagnose.attracted")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Attracted")}
                  {...(performanceDetail?.audience?.subMetric?.won || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(performanceDetail?.audience?.subMetric?.own || {})}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(performanceDetail?.audience?.subMetric?.own || {})}
                  text={t("pages.CareerDiagnose.personal")}
                  color="black"
                />
                <ComparisonExplain
                  performance={getKeyByGender("high", "female")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Personal")}
                  {...(performanceDetail?.audience?.subMetric?.own || {})}
                />
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-10"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="potencial-analysis">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.riskAnalysisTitle")}
              </h1>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={riscoFormula}
                  style={{ width: 400 }}
                  alt="Risco Fórmula"
                />
              </div>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                  marginTop: 32,
                }}
              >
                <Trans
                  i18nKey="pages.CareerDiagnose.riskAnalysisText1"
                  ns="radar"
                />
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div className="radar-chart-card">
                  <Charts.RadarChart
                    cor={comparisonColors}
                    width={60}
                    textColor="#fff"
                    legend={false}
                    labelFontSize="12px"
                    containerWidth="280px"
                    {...(riskIndex?.artistic?.metricChart || {
                      categories: [],
                      series: [],
                    })}
                    names={[
                      artistName,
                      t("pages.CareerDiagnose.minimumRequired"),
                    ]}
                    markerSize={2}
                    strokeWidth={1}
                    strokeColor="#fff"
                    max={5}
                    tickAmount={5}
                  />
                  {t("pages.CareerDiagnose.artistic")}
                  <ComparisonBox {...riskIndex?.artistic} />
                </div>
                <div className="radar-chart-card">
                  <Charts.RadarChart
                    cor={comparisonColors}
                    width={60}
                    textColor="#fff"
                    legend={false}
                    labelFontSize="12px"
                    containerWidth="280px"
                    {...(riskIndex?.team?.metricChart || {
                      categories: [],
                      series: [],
                    })}
                    names={[
                      artistName,
                      t("pages.CareerDiagnose.minimumRequired"),
                    ]}
                    markerSize={2}
                    strokeWidth={1}
                    strokeColor="#fff"
                    max={5}
                    tickAmount={5}
                  />
                  {t("pages.CareerDiagnose.team")}
                  <ComparisonBox {...riskIndex?.team} />
                </div>
                <div className="radar-chart-card">
                  <Charts.RadarChart
                    cor={["#FDB022"]}
                    width={60}
                    textColor="#fff"
                    legend={false}
                    labelFontSize="12px"
                    containerWidth="280px"
                    {...(riskIndex?.market?.metricChart || {
                      categories: [],
                      series: [],
                    })}
                    names={[
                      artistName,
                      t("pages.CareerDiagnose.minimumRequired"),
                    ]}
                    markerSize={2}
                    strokeWidth={1}
                    max={5}
                    tickAmount={5}
                    strokeColor="#fff"
                  />
                  {t("pages.CareerDiagnose.market")}
                  <div className="small comparator">
                    <span>
                      <b style={{ color: "#FDB022" }}>
                        {riskIndex?.market.recommentation?.toPrecision(2)}{" "}
                      </b>
                      {t("pages.CareerDiagnose.recommendation")}
                    </span>
                  </div>
                </div>
                <div className="radar-chart-card">
                  <Charts.RadarChart
                    cor={comparisonColors}
                    width={60}
                    textColor="#fff"
                    legend={false}
                    labelFontSize="12px"
                    containerWidth="280px"
                    {...(riskIndex?.investment?.metricChart || {
                      categories: [],
                      series: [],
                    })}
                    names={[
                      artistName,
                      t("pages.CareerDiagnose.minimumRequired"),
                    ]}
                    max={5}
                    tickAmount={5}
                    markerSize={2}
                    strokeWidth={1}
                    strokeColor="#fff"
                  />
                  {t("pages.CareerDiagnose.investment")}
                  <ComparisonBox {...riskIndex?.investment} />
                </div>
              </div>
              <MouseDisclaimer style={{ textAlign: "end", marginTop: 32 }} />
              <h1 className="texto-colorido title" style={{ marginTop: 32 }}>
                {t("pages.CareerDiagnose.riskAnalysisTitle")}
              </h1>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  color: "#000",
                }}
              >
                <Charts.LineChart
                  categories={normalDistribuition.xAxis}
                  series={[
                    {
                      data: normalDistribuition.yAxis,
                    },
                  ]}
                  min={0}
                  tickAmount={5}
                  height={400}
                  width={800}
                  showYAxisLabel={false}
                  showTooltip={false}
                  xaxisType="numeric"
                  showVerticalGridLines
                  color={["#F97066"]}
                  tooltip={{
                    label: t("pages.ArtistProfile.releasesLabel"),
                    points: [
                      {
                        text: t("pages.CareerDiagnose.youAreHere"),
                        showAsPointer: true,
                        x: riskIndex?.artistRiskIndex || 0,
                        y: normalDistribuition.yAxis[
                          normalDistribuition.xAxis.findIndex(
                            (num) => num === riskIndex?.artistRiskIndex
                          )
                        ],
                      },
                    ],
                  }}
                />
              </div>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                  marginTop: 32,
                }}
              >
                <Trans
                  i18nKey="pages.CareerDiagnose.riskAnalysisText2"
                  ns="radar"
                  values={{ artistRiskIndex: riskIndex?.artistRiskIndex || 0 }}
                />
              </p>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-10"
            id="dados-privados-11"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="artistic-colaboration">
              <h1 className="white-text title" style={{ color: "#000" }}>
                {t("pages.CareerDiagnose.noArtistColaborationTitle")}
              </h1>
              <p style={{ marginTop: 32, fontSize: 18 }}>
                <Trans
                  i18nKey="pages.CareerDiagnose.noArtistColaborationText"
                  ns="radar"
                />
              </p>
              <p style={{ fontSize: 18, margin: 0 }}>
                {riskIndex?.artistic?.evaluationOfAllPoints}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Charts.RadarChart
                  cor={comparisonColors}
                  containerWidth={1000}
                  height={550}
                  textColor="#000"
                  legend={false}
                  {...(riskIndex?.artistic?.metricChart || {
                    categories: [],
                    series: [],
                  })}
                  names={[
                    artistName,
                    t("pages.CareerDiagnose.minimumRequired"),
                  ]}
                  max={5}
                  tickAmount={5}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonBox big {...riskIndex?.artistic} />
              </div>
              <MouseDisclaimer style={{ textAlign: "end" }} />
              <div className="comparison-small">
                <div className="private-data-comparison-row">
                  <div className="comparison-chart">
                    <ComparisonBarChart
                      {...(riskIndex?.artistic?.subMetric?.voice || {})}
                      labelColor="#000"
                    />
                  </div>
                  <ComparisonValue
                    {...(riskIndex?.artistic?.subMetric?.voice || {})}
                    text={t("pages.CareerDiagnose.voice")}
                    color="black"
                  />
                  <ComparisonExplain
                    development={t("pages.CareerDiagnose.advanced")}
                    evolutionProcess={t("pages.CareerDiagnose.slow")}
                    evolutionCost={getKeyByGender("low")}
                    criteriaRelevance={getKeyByGender("high")}
                    {...getTipsByItem("Voice")}
                    {...(riskIndex?.artistic?.subMetric?.voice || {})}
                  />
                </div>
                <div className="private-data-comparison-row">
                  <div className="comparison-chart">
                    <ComparisonBarChart
                      {...(riskIndex?.artistic?.subMetric?.stage || {})}
                      labelColor="#000"
                    />
                  </div>
                  <ComparisonValue
                    {...(riskIndex?.artistic?.subMetric?.stage || {})}
                    text={t("pages.CareerDiagnose.stagePresence")}
                    color="black"
                  />
                  <ComparisonExplain
                    development={t("pages.CareerDiagnose.advanced")}
                    evolutionProcess={t("pages.CareerDiagnose.slow")}
                    evolutionCost={getKeyByGender("low")}
                    criteriaRelevance={getKeyByGender("high")}
                    {...getTipsByItem("StagePresence")}
                    {...(riskIndex?.artistic?.subMetric?.stage || {})}
                  />
                </div>
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-12"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ComparisonLegend artistName={artistName} />
            </div>
            <div className="comparison-small">
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.artistic?.subMetric?.composition || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.artistic?.subMetric?.composition || {})}
                  green
                  text={t("pages.CareerDiagnose.composition")}
                  color="white"
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Composition")}
                  {...(riskIndex?.artistic?.subMetric?.composition || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.artistic?.subMetric?.musicality || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.artistic?.subMetric?.musicality || {})}
                  green
                  text={t("pages.CareerDiagnose.musicality")}
                  color="white"
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Musicality")}
                  {...(riskIndex?.artistic?.subMetric?.musicality || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.artistic?.subMetric?.entrepreneurship ||
                      {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.artistic?.subMetric?.entrepreneurship || {})}
                  green
                  text={t("pages.CareerDiagnose.entreprenership")}
                  fontSize={18}
                  color="white"
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Entreprenership")}
                  {...(riskIndex?.artistic?.subMetric?.entrepreneurship || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.artistic?.subMetric?.image || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.artistic?.subMetric?.image || {})}
                  green
                  text={t("pages.CareerDiagnose.image")}
                  color="white"
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.fast")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Image")}
                  {...(riskIndex?.artistic?.subMetric?.image || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.artistic?.subMetric?.authenticity || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.artistic?.subMetric?.authenticity || {})}
                  green
                  text={t("pages.CareerDiagnose.authenticity")}
                  color="white"
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Authenticity")}
                  {...(riskIndex?.artistic?.subMetric?.authenticity || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.artistic?.subMetric?.health || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.artistic?.subMetric?.health || {})}
                  green
                  text={t("pages.CareerDiagnose.health")}
                  color="white"
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Health")}
                  {...(riskIndex?.artistic?.subMetric?.health || {})}
                />
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-13"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="team-qualification">
              <h1 className="white-text title">
                {t("pages.CareerDiagnose.teamQualificationTitle")}
              </h1>
              <p style={{ marginTop: 12, fontSize: 18 }}>
                <Trans
                  i18nKey="pages.CareerDiagnose.teamQualificationText"
                  ns="radar"
                />
              </p>
              <p style={{ fontSize: 18, margin: 0 }}>
                {riskIndex?.team?.evaluationOfAllPoints}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Charts.RadarChart
                  cor={comparisonColors}
                  containerWidth={1000}
                  height={550}
                  textColor="#000"
                  legend={false}
                  {...(riskIndex?.team?.metricChart || {
                    categories: [],
                    series: [],
                  })}
                  names={[
                    artistName,
                    t("pages.CareerDiagnose.minimumRequired"),
                  ]}
                  max={5}
                  tickAmount={5}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonBox big {...riskIndex?.team} />
              </div>
              <MouseDisclaimer style={{ textAlign: "end", marginTop: 12 }} />
              <span style={{ width: 440, marginTop: -20 }}>
                {t("pages.CareerDiagnose.teamMetricDisclaimer")}
              </span>
              <div className="comparison-small">
                <div className="private-data-comparison-row">
                  <div className="comparison-chart">
                    <ComparisonBarChart
                      {...(riskIndex?.team?.subMetric?.network || {})}
                      labelColor="#000"
                    />
                  </div>
                  <ComparisonValue
                    {...(riskIndex?.team?.subMetric?.network || {})}
                    text={t("pages.CareerDiagnose.network")}
                    color="black"
                  />
                  <ComparisonExplain
                    development={t("pages.CareerDiagnose.advanced")}
                    evolutionProcess={t("pages.CareerDiagnose.slow")}
                    evolutionCost={getKeyByGender("low")}
                    criteriaRelevance={getKeyByGender("high")}
                    {...getTipsByItem("Network")}
                    {...(riskIndex?.team?.subMetric?.network || {})}
                  />
                </div>
                <div className="private-data-comparison-row">
                  <div className="comparison-chart">
                    <ComparisonBarChart
                      {...(riskIndex?.team?.subMetric?.specialty || {})}
                      labelColor="#000"
                    />
                  </div>
                  <ComparisonValue
                    {...(riskIndex?.team?.subMetric?.specialty || {})}
                    text={t("pages.CareerDiagnose.specialty")}
                    color="black"
                  />
                  <ComparisonExplain
                    development={t("pages.CareerDiagnose.advanced")}
                    evolutionProcess={t("pages.CareerDiagnose.slow")}
                    evolutionCost={getKeyByGender("medium")}
                    criteriaRelevance={getKeyByGender("high")}
                    {...getTipsByItem("Specialty")}
                    {...(riskIndex?.team?.subMetric?.specialty || {})}
                  />
                </div>
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-1"
            id="dados-privados-14"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ComparisonLegend artistName={artistName} />
            </div>
            <div className="comparison-small">
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.team?.subMetric?.experience || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.team?.subMetric?.experience || {})}
                  text={t("pages.CareerDiagnose.experience")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.initial")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Experience1")}
                  {...(riskIndex?.team?.subMetric?.experience || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.team?.subMetric?.methodology || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.team?.subMetric?.methodology || {})}
                  text={t("pages.CareerDiagnose.methodology")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Methodology")}
                  {...(riskIndex?.team?.subMetric?.methodology || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.team?.subMetric?.pragmatism || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.team?.subMetric?.pragmatism || {})}
                  text={t("pages.CareerDiagnose.pragmatism")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.intermediate")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Pragmatism")}
                  {...(riskIndex?.team?.subMetric?.pragmatism || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.team?.subMetric?.planning || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.team?.subMetric?.planning || {})}
                  text={t("pages.CareerDiagnose.planning")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.intermediate")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Planning")}
                  {...(riskIndex?.team?.subMetric?.planning || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.team?.subMetric?.monitoring || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.team?.subMetric?.monitoring || {})}
                  text={t("pages.CareerDiagnose.monitoring")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.fast")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Monitoring")}
                  {...(riskIndex?.team?.subMetric?.monitoring || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.team?.subMetric?.governance || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.team?.subMetric?.governance || {})}
                  text={t("pages.CareerDiagnose.governance")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Governance")}
                  {...(riskIndex?.team?.subMetric?.governance || {})}
                />
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-14"
            id="dados-privados-15"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="suply-and-demand">
              <h1 className="white-text title">
                {t("pages.CareerDiagnose.suplyAndDemandTitle")}
              </h1>
              <p style={{ marginTop: 32, fontSize: 18 }}>
                <Trans
                  i18nKey="pages.CareerDiagnose.suplyAndDemandText"
                  ns="radar"
                />
              </p>
              <p style={{ fontSize: 18, margin: 0 }}>
                {riskIndex?.market?.evaluationOfAllPoints}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div
                  className="legenda"
                  style={{
                    margin: 12,
                    justifyContent: "space-around",
                    width: "100%",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <span className="label">
                        <span
                          className="square"
                          style={{
                            backgroundColor: "#FDB022",
                            fontWeight: 400,
                          }}
                        />
                        {t("pages.CareerDiagnose.genreMedian", {
                          genre: riskIndex?.market?.genreMetric,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Charts.RadarChart
                  cor={["#FDB022"]}
                  containerWidth={1126}
                  height={550}
                  textColor="#000"
                  legend={false}
                  {...(riskIndex?.market?.metricChart || {
                    categories: [],
                    series: [],
                  })}
                  names={[
                    artistName,
                    t("pages.CareerDiagnose.minimumRequired"),
                  ]}
                  max={5}
                  tickAmount={5}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div className="big comparator">
                  <span>
                    <b style={{ color: "#FDB022" }}>
                      {riskIndex?.market.recommentation?.toPrecision(2)}{" "}
                    </b>
                    {t("pages.CareerDiagnose.recommendation")}
                  </span>
                </div>
              </div>
              <MouseDisclaimer style={{ textAlign: "end", marginBottom: 12 }} />
              <span style={{ width: 440, marginTop: -20 }}>
                {t("pages.CareerDiagnose.marketMetricDisclaimer")}
              </span>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.market?.subMetric?.size || {})}
                    color={["#FDB022"]}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.market?.subMetric?.size || {})}
                  text={t("pages.CareerDiagnose.size")}
                  color="black"
                  useRecommendation
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost="-"
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Size")}
                  {...(riskIndex?.market?.subMetric?.size || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.market?.subMetric?.saturation || {})}
                    color={["#FDB022"]}
                    labelColor="#000"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.market?.subMetric?.saturation || {})}
                  text={t("pages.CareerDiagnose.saturation")}
                  color="black"
                  useRecommendation
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")} // TODO: change to medium?
                  evolutionCost="-"
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Saturation")}
                  {...(riskIndex?.market?.subMetric?.saturation || {})}
                />
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-15"
            id="dados-privados-16"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                className="legenda"
                style={{
                  margin: 32,
                  justifyContent: "space-around",
                  width: "100%",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <span className="label">
                      <span
                        className="square"
                        style={{
                          backgroundColor: "#FDB022",
                          fontWeight: 400,
                        }}
                      />
                      {t("pages.CareerDiagnose.genreMedian", {
                        genre: riskIndex?.market?.genreMetric,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="comparison-small">
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.market?.subMetric?.seasonality || {})}
                    labelColor="#fff"
                    color={["#FDB022"]}
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.market?.subMetric?.seasonality || {})}
                  text={t("pages.CareerDiagnose.seasonality")}
                  color="white"
                  useRecommendation
                  orange
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.intermediate")} // TODO check jean
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost="-"
                  criteriaRelevance={getKeyByGender("medium")}
                  {...getTipsByItem("Seasonality")}
                  {...(riskIndex?.market?.subMetric?.seasonality || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.market?.subMetric?.trend || {})}
                    labelColor="#fff"
                    color={["#FDB022"]}
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.market?.subMetric?.trend || {})}
                  text={t("pages.CareerDiagnose.tendency")}
                  color="white"
                  useRecommendation
                  orange
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost="-"
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Tendency")}
                  {...(riskIndex?.market?.subMetric?.trend || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.market?.subMetric?.behavior || {})}
                    labelColor="#fff"
                    color={["#FDB022"]}
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.market?.subMetric?.behavior || {})}
                  text={t("pages.CareerDiagnose.behavior")}
                  color="white"
                  useRecommendation
                  orange
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost="-"
                  criteriaRelevance={getKeyByGender("medium")}
                  {...getTipsByItem("Behavior")}
                  {...(riskIndex?.market?.subMetric?.behavior || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.market?.subMetric?.price || {})}
                    labelColor="#fff"
                    color={["#FDB022"]}
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.market?.subMetric?.price || {})}
                  text={t("pages.CareerDiagnose.price")}
                  color="white"
                  useRecommendation
                  orange
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.intermediate")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost="-"
                  criteriaRelevance={getKeyByGender("medium")}
                  {...getTipsByItem("Price")}
                  {...(riskIndex?.market?.subMetric?.price || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.market?.subMetric?.access || {})}
                    labelColor="#fff"
                    color={["#FDB022"]}
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.market?.subMetric?.access || {})}
                  text={t("pages.CareerDiagnose.access")}
                  color="white"
                  useRecommendation
                  orange
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.intermediate")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost="-"
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Access")}
                  {...(riskIndex?.market?.subMetric?.access || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.market?.subMetric?.maturity || {})}
                    labelColor="#fff"
                    color={["#FDB022"]}
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.market?.subMetric?.maturity || {})}
                  text={t("pages.CareerDiagnose.maturity")}
                  color="white"
                  useRecommendation
                  orange
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.intermediate")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost="-"
                  criteriaRelevance={getKeyByGender("medium")}
                  {...getTipsByItem("Maturity")}
                  {...(riskIndex?.market?.subMetric?.maturity || {})}
                />
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-16"
            id="dados-privados-17"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="necessary-impulse">
              <h1 className="white-text title">
                {t("pages.CareerDiagnose.necessaryImpulseTitle")}
              </h1>
              <p style={{ marginTop: 32, marginBottom: 12, fontSize: 17 }}>
                <Trans
                  i18nKey="pages.CareerDiagnose.necessaryImpulseText"
                  ns="radar"
                />
              </p>
              <p style={{ fontSize: 17, margin: 0 }}>
                {riskIndex?.investment?.evaluationOfAllPoints}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonLegend artistName={artistName} />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Charts.RadarChart
                  cor={comparisonColors}
                  containerWidth={1000}
                  height={550}
                  textColor="#000"
                  legend={false}
                  {...(riskIndex?.investment?.metricChart || {
                    categories: [],
                    series: [],
                  })}
                  names={[
                    artistName,
                    t("pages.CareerDiagnose.minimumRequired"),
                  ]}
                  max={5}
                  tickAmount={5}
                />
              </div>
              <MouseDisclaimer style={{ textAlign: "end", marginBottom: 12 }} />
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ComparisonBox big {...riskIndex?.investment} />
              </div>
              <div className="comparison-small">
                <div className="private-data-comparison-row">
                  <div className="comparison-chart">
                    <ComparisonBarChart
                      {...(riskIndex?.investment?.subMetric?.consistency || {})}
                      labelColor="#000"
                    />
                  </div>
                  <ComparisonValue
                    {...(riskIndex?.investment?.subMetric?.consistency || {})}
                    text={t("pages.CareerDiagnose.consistency")}
                    color="black"
                  />
                  <ComparisonExplain
                    development={t("pages.CareerDiagnose.intermediate")}
                    evolutionProcess={t("pages.CareerDiagnose.fast")}
                    evolutionCost={getKeyByGender("high")}
                    criteriaRelevance={getKeyByGender("high")}
                    {...getTipsByItem("Consistency")}
                    {...(riskIndex?.investment?.subMetric?.consistency || {})}
                  />
                </div>
                <div className="private-data-comparison-row">
                  <div className="comparison-chart">
                    <ComparisonBarChart
                      {...(riskIndex?.investment?.subMetric?.understanding ||
                        {})}
                      labelColor="#000"
                    />
                  </div>
                  <ComparisonValue
                    {...(riskIndex?.investment?.subMetric?.understanding || {})}
                    text={t("pages.CareerDiagnose.understanding")}
                    color="black"
                  />
                  <ComparisonExplain
                    development={t("pages.CareerDiagnose.advanced")}
                    evolutionProcess={t("pages.CareerDiagnose.slow")}
                    evolutionCost={getKeyByGender("medium")}
                    criteriaRelevance={getKeyByGender("high")}
                    {...getTipsByItem("Understanding")}
                    {...(riskIndex?.investment?.subMetric?.understanding || {})}
                  />
                </div>
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="private-data-17"
            id="dados-privados-18"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ComparisonLegend artistName={artistName} />
            </div>
            <div className="comparison-small">
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.investment?.subMetric?.experience || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.investment?.subMetric?.experience || {})}
                  text={t("pages.CareerDiagnose.experience")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.intermediate")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("medium")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Experience2")}
                  {...(riskIndex?.investment?.subMetric?.experience || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.investment?.subMetric?.compatibility || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.investment?.subMetric?.compatibility || {})}
                  text={t("pages.CareerDiagnose.compatibility")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={t("pages.CareerDiagnose.fast")}
                  evolutionCost={getKeyByGender("high")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Compatibility")}
                  {...(riskIndex?.investment?.subMetric?.compatibility || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.investment?.subMetric?.diversification ||
                      {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.investment?.subMetric?.diversification || {})}
                  text={t("pages.CareerDiagnose.diversification")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.intermediate")}
                  evolutionProcess={t("pages.CareerDiagnose.fast")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("low")}
                  {...getTipsByItem("Diversification")}
                  {...(riskIndex?.investment?.subMetric?.diversification || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.investment?.subMetric?.motivation || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.investment?.subMetric?.motivation || {})}
                  text={t("pages.CareerDiagnose.motivation")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.advanced")}
                  evolutionProcess={getKeyByGender("medium")}
                  evolutionCost={getKeyByGender("high")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Motivation")}
                  {...(riskIndex?.investment?.subMetric?.motivation || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.investment?.subMetric?.impact || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.investment?.subMetric?.impact || {})}
                  text={t("pages.CareerDiagnose.impact")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.intermediate")}
                  evolutionProcess={t("pages.CareerDiagnose.slow")}
                  evolutionCost={getKeyByGender("high")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Impact")}
                  {...(riskIndex?.investment?.subMetric?.impact || {})}
                />
              </div>
              <div className="private-data-comparison-row">
                <div className="comparison-chart">
                  <ComparisonBarChart
                    {...(riskIndex?.investment?.subMetric?.autonomy || {})}
                    labelColor="#fff"
                  />
                </div>
                <ComparisonValue
                  {...(riskIndex?.investment?.subMetric?.autonomy || {})}
                  text={t("pages.CareerDiagnose.autonomy")}
                  color="white"
                  green
                />
                <ComparisonExplain
                  development={t("pages.CareerDiagnose.initial")}
                  evolutionProcess={t("pages.CareerDiagnose.fast")}
                  evolutionCost={getKeyByGender("low")}
                  criteriaRelevance={getKeyByGender("high")}
                  {...getTipsByItem("Autonomy")}
                  {...(riskIndex?.investment?.subMetric?.autonomy || {})}
                />
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportChapterCover
            id="conclusao-1"
            title={t("pages.CareerDiagnose.conclusionTitle")}
          />
          <ExportCareerDiagnosePage
            className="conclusion-1"
            id="conclusao-2"
            shade="black"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="potencial-analysis">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.momentumTitle")}
              </h1>
              <h3 style={{ fontSize: 24, fontWeight: "bold", marginTop: 32 }}>
                {t("pages.CareerDiagnose.strmIndex")}
              </h3>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                  marginTop: 32,
                }}
              >
                {t("pages.CareerDiagnose.momentumText")}
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Charts.GaugeChart value={momentumIndex || 0} height={175} />
                <div className="legenda">
                  {gaugeValues.map((info: any, index) => (
                    <div
                      key={`color_${index}`}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <span className="label">
                          <span
                            className="square"
                            style={{
                              backgroundColor: info.color,
                              fontWeight: 400,
                            }}
                          >
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>
                          {t(`pages.CareerDiagnose.${info.text}`, {
                            context: "male",
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <h1
                className="texto-colorido title"
                style={{ marginTop: 72, marginBottom: 0 }}
              >
                {t("pages.CareerDiagnose.strengthsAndCriticalPointsTitle")}
              </h1>
              <h3 style={{ fontSize: 24, fontWeight: "bold", margin: 0 }}>
                {t("pages.CareerDiagnose.realization")}
              </h3>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                  marginTop: 12,
                }}
              >
                <Trans
                  i18nKey="pages.CareerDiagnose.strengthsAndCriticalPointsText"
                  ns="radar"
                />
              </p>
              <div className="strengths-critical-container">
                <h1 className="strengths-critical-title red">
                  {t("pages.CareerDiagnose.criticalPointsTitle")}
                </h1>
                <p className="strengths-critical-text">
                  <Trans
                    i18nKey="pages.CareerDiagnose.criticalPointsText"
                    ns="radar"
                  />
                </p>
              </div>
              {criticalPoints?.part1?.length > 0 ? (
                <div className="strengths-critical-container-number">
                  <h1 className="strengths-critical-title red small">
                    {t("pages.CareerDiagnose.potential")}
                  </h1>
                  <p className="strengths-critical-text">
                    {criticalPoints?.part1?.map((point: any, index: number) => (
                      <span
                        key={`critical_1_${index}`}
                        style={{ marginRight: 12 }}
                      >
                        <b>
                          {capitalize(
                            t(`pages.CareerDiagnose.${point.metric}`)
                          )}
                        </b>
                        {` `}=
                        {point.subMetrics?.map((metric: any, index: number) => (
                          <>
                            {index === 0
                              ? " "
                              : index === point.subMetrics.length - 1
                              ? " e "
                              : ", "}{" "}
                            {capitalize(metric)}
                            {index === point.subMetrics.length - 1 && "."}
                          </>
                        ))}
                      </span>
                    ))}
                  </p>
                </div>
              ) : (
                <CriticalStrongPointsEmpty
                  color="red"
                  point={t("pages.CareerDiagnose.potential")}
                  text={t("pages.CareerDiagnose.crititalPointsEmpty", {
                    criteria: t("pages.CareerDiagnose.potential"),
                  })}
                />
              )}
              {criticalPoints?.part2?.length > 0 ? (
                <div className="strengths-critical-container-number">
                  <h1 className="strengths-critical-title red small">
                    {t("pages.CareerDiagnose.risk")}
                  </h1>
                  <p className="strengths-critical-text">
                    {criticalPoints?.part2?.map((point: any, index: number) => (
                      <span
                        key={`critical_2_${index}`}
                        style={{ marginRight: 12 }}
                      >
                        <b>
                          {capitalize(
                            t(`pages.CareerDiagnose.${point.metric}`)
                          )}
                        </b>
                        {` `}=
                        {point.subMetrics?.map((metric: any, index: number) => (
                          <>
                            {index === 0
                              ? " "
                              : index === point.subMetrics.length - 1
                              ? " e "
                              : ", "}{" "}
                            {capitalize(metric)}
                            {index === point.subMetrics.length - 1 && "."}
                          </>
                        ))}
                      </span>
                    ))}
                  </p>
                </div>
              ) : (
                <CriticalStrongPointsEmpty
                  color="red"
                  point={t("pages.CareerDiagnose.risk")}
                  text={t("pages.CareerDiagnose.crititalPointsEmpty", {
                    criteria: t("pages.CareerDiagnose.risk"),
                  })}
                />
              )}
              <div className="strengths-critical-container">
                <h1 className="strengths-critical-title green">
                  {t("pages.CareerDiagnose.strengthsTitle")}
                </h1>
                <p className="strengths-critical-text">
                  <Trans
                    i18nKey="pages.CareerDiagnose.strengthsText"
                    ns="radar"
                  />
                </p>
              </div>
              <>
                {strengths?.part1?.length > 0 ? (
                  <div className="strengths-critical-container-number">
                    <h1 className="strengths-critical-title green small">
                      {t("pages.CareerDiagnose.potential")}
                    </h1>
                    <p className="strengths-critical-text">
                      {strengths?.part1?.map((point: any, index: number) => (
                        <span
                          style={{ marginRight: 12 }}
                          key={`strengths_01_${index}`}
                        >
                          <b>
                            {capitalize(
                              t(`pages.CareerDiagnose.${point.metric}`)
                            )}
                          </b>
                          {` `}=
                          {point.subMetrics?.map(
                            (metric: any, index: number) => (
                              <>
                                {index === 0
                                  ? " "
                                  : index === point.subMetrics.length - 1
                                  ? " e "
                                  : ", "}{" "}
                                {capitalize(metric)}
                                {index === point.subMetrics.length - 1 && "."}
                              </>
                            )
                          )}
                        </span>
                      ))}
                    </p>
                  </div>
                ) : (
                  <CriticalStrongPointsEmpty
                    point={t("pages.CareerDiagnose.potential")}
                    color="green"
                    text={t("pages.CareerDiagnose.strengthPointsEmpty", {
                      criteria: t("pages.CareerDiagnose.potential"),
                    })}
                  />
                )}
              </>
              <>
                {strengths?.part2?.length > 0 ? (
                  <div className="strengths-critical-container-number">
                    <h1 className="strengths-critical-title green small">
                      {t("pages.CareerDiagnose.risk")}
                    </h1>
                    <p className="strengths-critical-text">
                      {strengths?.part2?.map((point: any, index: number) => (
                        <span
                          style={{ marginRight: 12 }}
                          key={`strengths_02_${index}`}
                        >
                          <b>
                            {capitalize(
                              t(`pages.CareerDiagnose.${point.metric}`)
                            )}
                          </b>
                          {` `}=
                          {point.subMetrics?.map(
                            (metric: any, index: number) => (
                              <>
                                {index === 0
                                  ? " "
                                  : index === point.subMetrics.length - 1
                                  ? " e "
                                  : ", "}{" "}
                                {capitalize(metric)}
                                {index === point.subMetrics.length - 1 && "."}
                              </>
                            )
                          )}
                        </span>
                      ))}
                    </p>
                  </div>
                ) : (
                  <CriticalStrongPointsEmpty
                    point={t("pages.CareerDiagnose.risk")}
                    color="green"
                    text={t("pages.CareerDiagnose.strengthPointsEmpty", {
                      criteria: t("pages.CareerDiagnose.risk"),
                    })}
                  />
                )}
              </>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="conclusion-1"
            id="conclusao-3"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="potencial-analysis">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.channelAnalysisTitle")}
              </h1>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                  marginTop: 32,
                }}
              >
                <Trans
                  i18nKey="pages.CareerDiagnose.channelAnalysisText"
                  ns="radar"
                />
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={channelAnalysisImg}
                  style={{ width: 800 }}
                  alt="Análise de canais"
                />
              </div>
              <div className="legenda" style={{ marginTop: 64 }}>
                {channelAnalysisValues.map((info: any, index: number) => (
                  <div
                    key={`channel_analysis_${index}`}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <span className="label">
                        <span
                          className="square"
                          style={{
                            backgroundColor: info.color,
                            fontWeight: 400,
                          }}
                        >
                          &nbsp;&nbsp;&nbsp;&nbsp;
                        </span>
                        {t(`pages.CareerDiagnose.${info.text}`, {
                          context: "male",
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: 32,
                  backgroundImage: `url(${channelFocus})`,
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  width: "100%",
                  height: 710,
                }}
              >
                <Row style={{ marginTop: 72 }}>
                  <Col xs={12}>
                    <Row>
                      <Col xs={6} offset={4}>
                        <ChannelCircle
                          channel="VAGALUME"
                          status={channelAnalysis?.vagalume}
                        />
                      </Col>
                      <Col xs={6}>
                        <ChannelCircle
                          channel="INFLUENCERS"
                          status={channelAnalysis?.influencers}
                        />
                      </Col>
                      <Col xs={7}>
                        <ChannelCircle
                          channel="CIFRAS"
                          status={channelAnalysis?.cifras}
                        />
                      </Col>
                    </Row>
                    <Row style={{ marginTop: 3 }}>
                      <Col xs={6} offset={2}>
                        <ChannelCircle
                          channel="PALCO MP3"
                          status={channelAnalysis?.palcoMp3}
                        />
                      </Col>
                      <Col xs={6}>
                        <ChannelCircle
                          channel="SUA MÚSICA"
                          status={channelAnalysis?.suaMusica}
                        />
                      </Col>
                      <Col xs={8}>
                        <ChannelCircle
                          channel="SOUNDCLOUD"
                          status={channelAnalysis?.soundCloud}
                        />
                      </Col>
                    </Row>
                  </Col>

                  <Col xs={12}>
                    <Row>
                      <Col xs={6} offset={7}>
                        <ChannelCircle
                          channel="SPOTIFY"
                          status={channelAnalysis?.spotify}
                        />
                      </Col>
                      <Col xs={11}>
                        <ChannelCircle
                          channel="YOUTUBE"
                          status={channelAnalysis?.youtube}
                        />
                      </Col>
                    </Row>
                    <Row style={{ marginTop: 3 }} justify="center">
                      <Col xs={6} offset={2}>
                        <ChannelCircle
                          channel="SHOWS"
                          status={channelAnalysis?.shows}
                        />
                      </Col>
                      <Col xs={6}>
                        <ChannelCircle
                          channel="RADIO"
                          status={channelAnalysis?.radio}
                        />
                      </Col>
                      <Col xs={8}>
                        <ChannelCircle
                          channel="TELEVISÃO"
                          status={channelAnalysis?.televisao}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Row style={{ marginTop: 72 }}>
                  <Col xs={12}>
                    <Row>
                      <Col xs={6} offset={4}>
                        <ChannelCircle
                          channel="TIKTOK"
                          status={channelAnalysis?.tiktok}
                        />
                      </Col>
                      <Col xs={6}>
                        <ChannelCircle
                          channel="FACEBOOK"
                          status={channelAnalysis?.facebook}
                        />
                      </Col>
                    </Row>

                    <Row style={{ marginTop: 3 }}>
                      <Col xs={6} offset={7}>
                        <ChannelCircle
                          channel="INSTAGRAM"
                          status={channelAnalysis?.instagram}
                        />
                      </Col>
                      <Col xs={6}>
                        <ChannelCircle
                          channel="TWITTER"
                          status={channelAnalysis?.twitter}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={12}>
                    <Row>
                      <Col xs={6} offset={5}>
                        <ChannelCircle
                          channel="SITE"
                          status={channelAnalysis?.site}
                        />
                      </Col>
                      <Col xs={6}>
                        <ChannelCircle
                          channel="GAMES"
                          status={channelAnalysis?.games}
                        />
                      </Col>
                    </Row>

                    <Row style={{ marginTop: 3 }}>
                      <Col xs={6} offset={2}>
                        <ChannelCircle
                          channel="MERCHANDISING"
                          status={channelAnalysis?.merchandising}
                        />
                      </Col>
                      <Col xs={6}>
                        <ChannelCircle
                          channel="SYNC"
                          status={channelAnalysis?.sync}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            </div>
          </ExportCareerDiagnosePage>
          <ExportCareerDiagnosePage
            className="conclusion-1"
            id="conclusao-4"
            shade="white"
            artistName={artistName}
            lastUpdate={lastUpdate}
          >
            <div className="potencial-analysis">
              <h1 className="texto-colorido title">
                {t("pages.CareerDiagnose.revenueProfileTitle")}
              </h1>
              <h3 style={{ fontSize: 24, color: "#000" }}>
                <b>{t("pages.CareerDiagnose.revenueSources")}</b>
              </h3>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                  marginTop: 32,
                }}
              >
                <Trans
                  i18nKey="pages.CareerDiagnose.revenueProfileText"
                  ns="radar"
                />
              </p>
              <p
                style={{
                  textAlign: "justify",
                  fontSize: 18,
                  marginTop: 16,
                }}
              >
                <b>{t("pages.CareerDiagnose.revenueSourcesDisclaimer")}</b>
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  fontSize: 24,
                  margin: 32,
                }}
              >
                <span style={{ color: "#75C594" }}>
                  <StarOutlined /> {t("pages.CareerDiagnose.profile")}
                </span>
                <span
                  style={{ textTransform: "uppercase", fontWeight: "bold" }}
                >
                  {`${getRevenueProfile()}`}
                </span>
              </div>
              {profileRevenueInformed ? (
                <>
                  <div className="legenda" style={{ margin: 64 }}>
                    {revenueColorsValue.map((info: any, index: number) => (
                      <div
                        key={`revenue_${index}`}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <span className="label">
                            <span
                              className="square"
                              style={{
                                backgroundColor: info.color,
                                fontWeight: 400,
                              }}
                            >
                              &nbsp;&nbsp;&nbsp;&nbsp;
                            </span>
                            {t(`pages.CareerDiagnose.${info.text}`, {
                              context: "male",
                            })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Charts.RangeColumnChart
                      {...getRevenueInfo()}
                      height={500}
                      width={900}
                      fontSize="14px"
                      colors={[
                        getrevenueColorsByindex(0),
                        getrevenueColorsByindex(
                          revenueProfile?.composer?.status || 0,
                          1
                        ),
                        getrevenueColorsByindex(
                          revenueProfile?.shows?.status || 0,
                          1
                        ),
                        getrevenueColorsByindex(
                          revenueProfile?.publicity?.status || 0,
                          1
                        ),
                        getrevenueColorsByindex(
                          revenueProfile?.streaming?.status || 0,
                          1
                        ),
                      ]}
                    />
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 72,
                  }}
                >
                  <span style={{ fontSize: 24 }}>
                    {t("pages.CareerDiagnose.noRevenueInfo")}
                  </span>
                </div>
              )}
            </div>
          </ExportCareerDiagnosePage>
        </div>
      </Dropdown>
    </Spin>
  );
};

export default CareerDiagnose;
